import { SPUR_EXPERIENCES_BRAND } from '@apps/registry/common/util/constants';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { maybeRender } from '@shared/utils/functions';
import type { BoxProps } from '@withjoy/joykit';
import { Box, Flex, TextV2, useTheme } from '@withjoy/joykit';
import React, { useCallback, useRef } from 'react';
import { Breadcrumbs } from '..';
import type { UseSharedCatalogMutationReturn } from '../../Catalog.controller';
import { useCatalogRegistryTranslations } from '../../Catalog.i18n';
import type { CatalogItemTile, CatalogProductItemDetails } from '../../Catalog.types';
import { CatalogProductItemTags } from '../../Catalog.types';
import { PdpSections } from '../../components/PdpSections/Sections/PdpSection';
import { isDetailsSectionType } from '../../components/PdpSections/Sections/PdpSection/PdpSections.utils';
import { removePhysicalProductDetailSections } from './ProductDetails.utils';
import type { ProductDetailsControllerArgs } from './ProductDetails.controller';
import { useProductDetailsController } from './ProductDetails.controller';
import { Grid, StyledBody, StyledFooter, StyledHeader, StyledLeftColumn, StyledRightColumn, styles } from './ProductDetails.styles';
import { AboutTabs, ProductTitles } from './components';
import { BuyForm } from './components/BuyForm';
import { PdpValuePropsSection } from '../../routes/CatalogProductsIndex/components/PdpValuePropsSection/PdpValuePropsSection';
import { ProductCatalogStorefrontStatus } from '@graphql/generated-base';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { ProductImageGallery } from '@apps/registry/common/components/Catalog/components/ProductDetails/components/ProductImageGallery';
import { DetailSections } from '../PdpSections/Sections/DetailSections/DetailSections';
import { getMediaAssetImgUrl } from './components/ProductImageGallery/utils';
import { CustomerReviewsLazy } from './components/CustomerReviews/CustomerReviews.lazy';
import { CustomerReviewsV2Lazy } from './components/CustomerReviewsV2/CustomerReviewsV2.lazy';
import { createGlobalStyle, pxToRem } from '@withjoy/joykit/theme';
import { CashfundAddToRegistryButton } from './components/CashfundAddToRegistryButton/CashfundAddToRegistryButton';
import { isCashOrCharityCatalogItem } from '../../utils/interpretItemType';
import { useResolvedCurrencyCode } from '@shared/hooks/useResolvedCurrencyCode';
import { DEFAULT_CURRENCY } from '@shared/utils/currency/constants';
import { useIsShopOutOfStockBadgeEnabled } from '../../hooks/useIsShopOutOfStockBadgeEnabled';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';

const ProductDetailGlobalStyles = createGlobalStyle`
  html {
    scroll-behavior: unset!important;
  }
`;

const CUSTOMER_REVIEWS_ELEMENT_ID = 'reviews-scroll-element';

export type AddCatalogItemPayload = Parameters<UseSharedCatalogMutationReturn['addCatalogItemToRegistry']>[0]; //{ catalogItemId?: string; qty?: number };

export interface ProductDetailsProps {
  product: Maybe<CatalogProductItemDetails>;
  addCatalogItem: (values: AddCatalogItemPayload, onDone?: () => void) => void;
  addCatalogItemLoading: boolean;

  isItemAddedToRegistry: boolean;
  showFooter?: boolean;
  onDone?: () => void;
  showSkeleton?: boolean;
  defaultDepartmentTitle: string;
  isDialog: boolean;
  onHide?: () => void;
  handleHover: (id?: string, isHoverExit?: boolean) => void;
}

// ------------------------------
// Layout Components

const Page: React.FC<{ isMobile?: boolean }> = ({ children, isMobile }) => {
  return (
    <Grid
      maxWidth="100%"
      width={'100%'}
      isMobile={isMobile}
    >
      {children}
    </Grid>
  );
};

const Header = ({ children, isMobile }: { children: React.ReactNode; isMobile?: boolean }) => {
  return <StyledHeader isMobile={isMobile}>{children}</StyledHeader>;
};

const Body = React.forwardRef<HTMLDivElement, BoxProps & { isMobile?: boolean }>((props, ref) => {
  return (
    <StyledBody
      __css={styles.body({ isMobile: props.isMobile })}
      {...props}
      ref={ref}
    />
  );
});

const LeftColumn = (props: { children: React.ReactNode }) => {
  return (
    <StyledLeftColumn
      stack="vertical"
      spacing={8}
      __css={styles.leftColumn}
    >
      {props.children}
    </StyledLeftColumn>
  );
};

const RightColumn = ({ children, isMobile }: { children: React.ReactNode; isMobile?: boolean }) => {
  return (
    <StyledRightColumn
      isMobile={isMobile}
      __css={styles.rightColumn}
    >
      <Box
        width="100%"
        alignItems="flex-start"
      >
        {children}
      </Box>
    </StyledRightColumn>
  );
};

const Footer = (props: BoxProps) => {
  return (
    <StyledFooter
      width="100%"
      marginTop={'50px'}
      {...props}
    />
  );
};

const ProductDetailsRoot: React.FC<ProductDetailsProps> = ({
  product,
  addCatalogItem: baseAddCatalogItem,
  addCatalogItemLoading,
  onDone,
  isDialog,
  showSkeleton,
  handleHover,
  isItemAddedToRegistry,
}) => {
  const addCatalogItem: ProductDetailsControllerArgs['addCatalogItem'] = useCallback<Required<ProductDetailsControllerArgs>['addCatalogItem']>(
    async args => {
      baseAddCatalogItem(args, onDone);
    },
    [baseAddCatalogItem, onDone],
  );

  const { colors, space } = useTheme();

  const {
    createProductTabs,
    selectedProduct,
    isMobile,
    handleOnClickMediaAsset,
    mediaAssets,
    selectedMediaAsset,
    telemetryPageValue,
    setProductSelected,
    getSectionExtraArgsByType,
    handleBrandClick,
    onOpenEditGiftDialog,
    registryCurrency,
  } = useProductDetailsController({
    product,
    addCatalogItem,
    isDialog,
  });

  const resolvedCurrencyCode = useResolvedCurrencyCode(registryCurrency?.code);

  const { getProductDetailPageTranslations } = useCatalogRegistryTranslations();
  const { fulfilledBy, joyExclusive } = getProductDetailPageTranslations();
  const retailerName = selectedProduct?.retailer?.name;
  const tabItems = createProductTabs();
  const isCoverImage = product?.brand?.name === SPUR_EXPERIENCES_BRAND;
  const category = selectedProduct?.subCategories?.[0] || null;

  const pdpBodyRef = useRef(null);
  const galleryLightboxQuickAddBarRef = useRef(null);
  const isOutOfStockBadgeEnabled = useIsShopOutOfStockBadgeEnabled();
  const [isReviewsRefreshEnabled] = useIsFeatureEnabled('registryPdpReviewsRefreshEnabled');

  if (!product) return null;
  const storefrontStatus = selectedProduct?.storefrontInfo.storefrontStatus;
  const isProductAvailable = storefrontStatus === ProductCatalogStorefrontStatus.available;
  const isTypeCashOrCharity = isCashOrCharityCatalogItem(product);
  const allDetailsSections = product?.pdpSections?.filter(isDetailsSectionType);
  const detailsSections = allDetailsSections && isTypeCashOrCharity ? removePhysicalProductDetailSections(allDetailsSections) : allDetailsSections;
  const nonDetailsSections = product?.pdpSections?.filter(section => section.sectionType !== 'details');
  const joyExclusiveLabel = selectedProduct?.tags.includes(CatalogProductItemTags.joyExclusive) ? joyExclusive : null;
  const cashItemCurrencyCode = isTypeCashOrCharity ? resolvedCurrencyCode : undefined;
  const isNonUsdCashFund = isTypeCashOrCharity && resolvedCurrencyCode.toUpperCase() !== DEFAULT_CURRENCY;
  const { storefrontInfo } = product;
  const isOutOfStock = isOutOfStockBadgeEnabled && product.maxQty === 0;
  const customerReviewsProps: React.ComponentProps<typeof CustomerReviewsLazy> = {
    id: CUSTOMER_REVIEWS_ELEMENT_ID,
    catalogItemId: product.id,
    marginTop: [pxToRem(92), null, pxToRem(120)],
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      flexDirection={'column'}
      alignItems={'center'}
    >
      <ProductDetailGlobalStyles />
      <Page isMobile={isMobile}>
        <Header isMobile={isMobile}>
          <Breadcrumbs
            defaultAlign="left"
            hideLastBreadcrumb={false}
            isMobile={isMobile}
            categoryId={category?.id}
            telemetryPageValue={telemetryPageValue}
            allowNavigationOnLastBreadcrumb={true}
          />
        </Header>
        <Body isMobile={isMobile}>
          <LeftColumn>
            {maybeRender(
              isMobile,
              <ProductTitles
                catalogItemId={selectedProduct?.id}
                title={selectedProduct?.brand?.name}
                badgeLabel={joyExclusiveLabel}
                summary={selectedProduct?.title}
                priceFloatingPointDecimalString={selectedProduct?.fullPrice?.floatingPointDecimalString}
                priceCurrencyCode={cashItemCurrencyCode ?? selectedProduct?.fullPrice?.currency?.code}
                salesPrice={selectedProduct?.price?.floatingPointDecimalString}
                salesPriceCurrenyCode={selectedProduct?.price?.currency?.code}
                storefrontStatus={storefrontStatus}
                onBrandUrlClick={handleBrandClick}
                ratingStats={selectedProduct?.rating?.stats}
                productRatingScrollToElementId={CUSTOMER_REVIEWS_ELEMENT_ID}
                isFullPrice={Boolean(selectedProduct?.isFullPrice)}
                itemType={selectedProduct?.itemType || ''}
              />,
            )}
            <ProductImageGallery
              mediaAssets={mediaAssets}
              selectedMediaAsset={selectedMediaAsset}
              handleClickMediaAsset={handleOnClickMediaAsset}
              isCoverImage={isCoverImage}
              isDialog={isDialog}
              galleryLightboxQuickAddBarRef={galleryLightboxQuickAddBarRef}
              isOutOfStock={isOutOfStock}
            />
          </LeftColumn>

          <RightColumn isMobile={isMobile}>
            <Box
              width="100%"
              alignItems="flex-start"
            >
              {!isMobile ? (
                <ProductTitles
                  catalogItemId={selectedProduct?.id}
                  title={selectedProduct?.brand?.name}
                  badgeLabel={joyExclusiveLabel}
                  summary={selectedProduct?.title}
                  priceFloatingPointDecimalString={selectedProduct?.fullPrice?.floatingPointDecimalString}
                  salesPrice={selectedProduct?.price?.floatingPointDecimalString}
                  salesPriceCurrenyCode={selectedProduct?.price?.currency?.code}
                  priceCurrencyCode={cashItemCurrencyCode ?? selectedProduct?.fullPrice?.currency?.code}
                  storefrontStatus={selectedProduct?.storefrontInfo.storefrontStatus}
                  onBrandUrlClick={handleBrandClick}
                  ratingStats={selectedProduct?.rating?.stats}
                  productRatingScrollToElementId={CUSTOMER_REVIEWS_ELEMENT_ID}
                  isFullPrice={Boolean(selectedProduct?.isFullPrice)}
                  itemType={selectedProduct?.itemType || ''}
                />
              ) : undefined}

              {isTypeCashOrCharity ? (
                <CashfundAddToRegistryButton
                  isAddingItem={addCatalogItemLoading}
                  selectedProduct={selectedProduct as CatalogItemTile}
                  disabled={!storefrontInfo?.canAddToRegistry}
                />
              ) : (
                <>
                  {/* NREG-1983: PDP only. Hide discontinued swatches on single-dim products. Quick-add does not pass this prop. */}
                  <BuyForm
                    isMobile={isMobile}
                    isAddingItem={addCatalogItemLoading}
                    showAllDimensions={true}
                    hideDiscontinuedSwatches={true}
                    relatedItems={product?.group?.items}
                    product={product}
                    selectedProduct={selectedProduct}
                    addCatalogItem={addCatalogItem}
                    setProductSelected={setProductSelected}
                    dimensionSpacing={6}
                    colorSwatchVariant="grid"
                    selectedImage={getMediaAssetImgUrl(selectedMediaAsset) || getMediaAssetImgUrl(product?.mediaAssets?.[0])}
                    showSkeleton={showSkeleton}
                    stickyBarIntersectionRef={pdpBodyRef}
                    galleryLightboxQuickAddBarRef={galleryLightboxQuickAddBarRef}
                    handleHover={handleHover}
                    isItemAddedToRegistry={isItemAddedToRegistry}
                    setOpenGiftDialog={() => product?.id && onOpenEditGiftDialog(product?.id)}
                  />
                </>
              )}
            </Box>
            {retailerName && isProductAvailable && (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                marginTop={space[5]}
                color={colors.mono12}
              >
                <TextV2 typographyVariant="label1">
                  {fulfilledBy} {retailerName}
                </TextV2>
              </Box>
            )}
            {!isNonUsdCashFund && <PdpValuePropsSection />}
            {detailsSections && (
              <PdpSections
                sections={detailsSections}
                getSectionExtraArgsByType={getSectionExtraArgsByType}
                key={product.id}
              />
            )}
          </RightColumn>
        </Body>
      </Page>
      <Footer
        justifyContent="center"
        flexDirection={'column'}
      >
        {nonDetailsSections ? (
          <PdpSections
            sections={nonDetailsSections}
            getSectionExtraArgsByType={getSectionExtraArgsByType}
            key={product.id}
          />
        ) : (
          <AboutTabs tabsInfo={tabItems} />
        )}
        {isReviewsRefreshEnabled ? <CustomerReviewsV2Lazy {...customerReviewsProps} /> : <CustomerReviewsLazy {...customerReviewsProps} />}
      </Footer>
    </Box>
  );
};

export const ProductDetails: React.FC<ProductDetailsProps> = props => {
  const { showSkeleton } = props;
  const isMobile = useIsMobileScreen();

  return (
    <>
      <SkeletonGroup
        isReady={!showSkeleton}
        placeholder={
          <Flex justifyContent={'center'}>
            <Page isMobile={isMobile}>
              <Header isMobile={isMobile}>
                <Breadcrumbs.Skeleton marginTop={6} />
              </Header>
              <Body
                marginTop={6}
                isMobile={isMobile}
              >
                <LeftColumn>
                  {isMobile && <ProductTitles.Skeleton />}
                  <ProductImageGallery.Skeleton isMobile={isMobile} />
                </LeftColumn>

                <RightColumn isMobile={isMobile}>
                  {!isMobile && <ProductTitles.Skeleton />}
                  <BuyForm.Skeleton mode="full" />
                  <PdpValuePropsSection.Skeleton />
                  <DetailSections.Skeleton />
                </RightColumn>
              </Body>
            </Page>
          </Flex>
        }
      >
        <ProductDetailsRoot {...props} />
      </SkeletonGroup>
    </>
  );
};
